import { EditOutlined, PrinterOutlined } from '@ant-design/icons';
import {
  byCommitteeRole,
  COMMITTEE_ROLE_LABELS,
  type CommitteeRole,
  cleanPhone,
  describeError,
  fullDate,
  mandateNotice,
  present,
  today,
} from '@horof/shared';
import { Alert, App, Button, DatePicker, Form, Input, Skeleton } from 'antd';
import dayjs from 'dayjs';
import { type ReactNode, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery } from 'urql';
import {
  type Profile,
  ProfileDocument,
  UpdateProfileDocument,
} from './association-data';
import { PageHeader } from './ui';
import { useIsCommittee } from './water-data';

type TextKey =
  | 'associationName'
  | 'shortName'
  | 'address'
  | 'contactPhone'
  | 'email'
  | 'registrationNumber'
  | 'registrationAuthority'
  | 'taxId'
  | 'bankRib'
  | 'mission'
  | 'vision';
type DateKey = 'foundedOn' | 'registrationDate' | 'mandateStart' | 'mandateEnd';

type FormValues = Record<TextKey, string> & Record<DateKey, dayjs.Dayjs | null>;

const SECTIONS: {
  title: string;
  fields: { key: TextKey | DateKey; label: string; long?: boolean }[];
}[] = [
  {
    title: 'التعريف',
    fields: [
      { key: 'associationName', label: 'اسم الجمعية' },
      { key: 'shortName', label: 'الاسم المختصر' },
      { key: 'foundedOn', label: 'تاريخ التأسيس' },
      { key: 'address', label: 'العنوان' },
      { key: 'contactPhone', label: 'الهاتف' },
      { key: 'email', label: 'البريد الإلكتروني' },
    ],
  },
  {
    title: 'الوضع القانوني',
    fields: [
      { key: 'registrationNumber', label: 'رقم وصل الإيداع' },
      { key: 'registrationDate', label: 'تاريخ وصل الإيداع' },
      { key: 'registrationAuthority', label: 'الجهة المسلِّمة للوصل' },
      { key: 'taxId', label: 'التعريف الضريبي' },
      { key: 'bankRib', label: 'الحساب البنكي (RIB)' },
    ],
  },
  {
    title: 'الرسالة والرؤية',
    fields: [
      { key: 'mission', label: 'رسالة الجمعية', long: true },
      { key: 'vision', label: 'رؤية الجمعية', long: true },
    ],
  },
  {
    title: 'ولاية المكتب',
    fields: [
      { key: 'mandateStart', label: 'بداية الولاية' },
      { key: 'mandateEnd', label: 'نهاية الولاية' },
    ],
  },
];

const DATE_KEYS: DateKey[] = [
  'foundedOn',
  'registrationDate',
  'mandateStart',
  'mandateEnd',
];
const isDate = (k: string): k is DateKey => DATE_KEYS.includes(k as DateKey);

/**
 * «بطاقة الجمعية»: who the association is (arqam doc 52). A card to read and
 * print; the committee edits it. A red line when the committee's term ends
 * within 90 days or has ended, and the bureau's members.
 */
export function AssociationScreen() {
  const canEdit = useIsCommittee();
  const [editing, setEditing] = useState(false);
  const [{ data, error }] = useQuery({ query: ProfileDocument });
  const p = data?.currentSettings;
  const bureau = byCommitteeRole(
    present(data?.members?.nodes) as {
      id: string;
      fullName: string;
      phone: string | null;
      committeeRole: CommitteeRole | null;
    }[],
  );
  const notice = mandateNotice(p?.mandateEnd, today());

  if (error) {
    return (
      <section className="desk-page">
        <Alert
          type="error"
          showIcon
          message={describeError(error, 'تعذّر تحميل بطاقة الجمعية.')}
        />
      </section>
    );
  }
  if (!p) {
    return (
      <section className="desk-page">
        <Skeleton active paragraph={{ rows: 12 }} />
      </section>
    );
  }

  return (
    <section className="desk-page desk-association">
      <PageHeader
        title="بطاقة الجمعية"
        description="من هي الجمعية: التعريف، الوضع القانوني، الرسالة والرؤية، ولاية المكتب وأعضاؤه."
        actions={
          <>
            {!editing && (
              <Button icon={<PrinterOutlined />} onClick={() => window.print()}>
                طباعة
              </Button>
            )}
            {canEdit && !editing && (
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => setEditing(true)}
              >
                تعديل
              </Button>
            )}
          </>
        }
      />

      {notice && (
        <Alert
          type="error"
          showIcon
          className="desk-mandate"
          message={notice.text}
          description={
            notice.past
              ? `انتهت في ${fullDate(p.mandateEnd ?? '')}، منذ ${notice.days} يوماً.`
              : `تنتهي في ${fullDate(p.mandateEnd ?? '')}، بعد ${notice.days} يوماً.`
          }
        />
      )}

      {editing ? (
        <ProfileForm profile={p} onDone={() => setEditing(false)} />
      ) : (
        <article className="desk-profile">
          <header className="desk-profile-head">
            <h2>{p.associationName}</h2>
            {p.shortName && <div className="field-hint">{p.shortName}</div>}
          </header>
          {SECTIONS.map((section) => (
            <section key={section.title} className="desk-profile-section">
              <h3>{section.title}</h3>
              <dl>
                {section.fields.map((f) => (
                  <Row key={f.key} label={f.label} long={f.long}>
                    {show(p, f.key)}
                  </Row>
                ))}
              </dl>
            </section>
          ))}
          <section className="desk-profile-section">
            <h3>أعضاء المكتب</h3>
            {bureau.length === 0 ? (
              <div className="field-hint">
                لم يُحدَّد أعضاء المكتب. تُحدَّد الصفة من صفحة العضو في «الأعضاء».
              </div>
            ) : (
              <table className="desk-mini-table">
                <tbody>
                  {bureau.map((m) => (
                    <tr key={m.id}>
                      <td>
                        {m.committeeRole
                          ? COMMITTEE_ROLE_LABELS[m.committeeRole]
                          : ''}
                      </td>
                      <td>
                        <Link to={`/members/${m.id}`}>{m.fullName}</Link>
                      </td>
                      <td className="ltr-num">{m.phone}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
          <footer className="field-hint desk-profile-foot">
            آخر تحديث: {fullDate(p.updatedAt)}
          </footer>
        </article>
      )}
    </section>
  );
}

function Row({
  label,
  long,
  children,
}: {
  label: string;
  long?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={long ? 'is-long' : undefined}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function show(p: Profile, key: TextKey | DateKey): ReactNode {
  const v = p[key];
  if (!v) return <span className="field-hint">—</span>;
  if (isDate(key)) return fullDate(v);
  if (key === 'contactPhone' || key === 'bankRib' || key === 'taxId') {
    return <span className="ltr-num">{v}</span>;
  }
  return v;
}

function ProfileForm({
  profile: p,
  onDone,
}: {
  profile: Profile;
  onDone: () => void;
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const [problem, setProblem] = useState<string | null>(null);
  const [{ fetching }, update] = useMutation(UpdateProfileDocument);
  const initial = useMemo(() => {
    const v = {} as FormValues;
    for (const s of SECTIONS) {
      for (const f of s.fields) {
        if (isDate(f.key)) v[f.key] = p[f.key] ? dayjs(p[f.key]) : null;
        else v[f.key] = p[f.key] ?? '';
      }
    }
    return v;
  }, [p]);

  async function save(values: FormValues) {
    setProblem(null);
    const phone = cleanPhone(values.contactPhone ?? '');
    if ('error' in phone) return setProblem(phone.error);
    const email = values.email.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return setProblem('البريد الإلكتروني غير صحيح.');
    }
    const start = values.mandateStart;
    const end = values.mandateEnd;
    if (start && end && !end.isAfter(start)) {
      return setProblem('نهاية الولاية يجب أن تكون بعد بدايتها.');
    }
    const patch: Record<string, string | null> = {};
    for (const s of SECTIONS) {
      for (const f of s.fields) {
        const v = values[f.key];
        patch[f.key] = isDate(f.key)
          ? v
            ? (v as dayjs.Dayjs).format('YYYY-MM-DD')
            : null
          : (v as string).trim() || null;
      }
    }
    patch.contactPhone = phone.phone;
    patch.associationName = values.associationName.trim();
    const r = await update({ patch });
    if (r.error) {
      setProblem(describeError(r.error, 'تعذّر الحفظ.'));
      return;
    }
    message.success('حُفظت بطاقة الجمعية');
    onDone();
  }

  return (
    <Form<FormValues>
      form={form}
      layout="vertical"
      initialValues={initial}
      onFinish={save}
      className="desk-profile-form"
    >
      {SECTIONS.map((section) => (
        <fieldset key={section.title} className="desk-profile-section">
          <legend>{section.title}</legend>
          <div className="desk-profile-fields">
            {section.fields.map((f) => (
              <Form.Item
                key={f.key}
                name={f.key}
                label={f.label}
                className={f.long ? 'is-long' : undefined}
                rules={
                  f.key === 'associationName'
                    ? [
                        {
                          required: true,
                          whitespace: true,
                          message: 'الاسم إلزامي.',
                        },
                      ]
                    : undefined
                }
              >
                {isDate(f.key) ? (
                  <DatePicker format="YYYY-MM-DD" className="ltr-num" />
                ) : f.long ? (
                  <Input.TextArea autoSize={{ minRows: 3, maxRows: 10 }} />
                ) : (
                  <Input
                    className={
                      ['contactPhone', 'bankRib', 'taxId', 'email'].includes(
                        f.key,
                      )
                        ? 'ltr-num'
                        : undefined
                    }
                  />
                )}
              </Form.Item>
            ))}
          </div>
        </fieldset>
      ))}
      {problem && <Alert type="error" showIcon message={problem} />}
      <div className="desk-actions">
        <Button type="primary" htmlType="submit" loading={fetching}>
          حفظ
        </Button>
        <Button onClick={onDone}>إلغاء</Button>
      </div>
    </Form>
  );
}
